import React, {useCallback, useMemo, useState} from "react";
import {createColumnHelper} from "@tanstack/react-table";
import DataGrid from "../DataGrid/DataGrid.jsx";
import {ABC_COLORS, formatEuro, formatQuantity, OTHER_GROUP_COLOR} from "./format.js";
import {RecommendationBadge, RECOMMENDATION_META, StockByWarehousePopover, formatPallets, withAssumptionParams} from "./inventoryStrategyShared.jsx";

const columnHelper = createColumnHelper();

const RECOMMENDATION_SELECT_OPTIONS = [
    {value: "", label: "Alle"},
    ...Object.entries(RECOMMENDATION_META).map(([value, meta]) => ({value, label: meta.label})),
];

/**
 * Mengenempfehlung je Artikel (#3 im Auftrag) - das Hauptelement der
 * Lagerstrategie-Seite. Strukturgleich zu DetailDataGrid.jsx (serverseitiges
 * Paging/Sortieren/Filtern/Gruppieren über DataGrid, Excel-Export), aber mit
 * den in InventoryStrategyQuery::buildArticleRow() berechneten Feldern.
 *
 * "target_stock" (empfohlener Zielbestand) ist laut Auftrag die wichtigste
 * Spalte - sie ist deshalb optisch hervorgehoben (fette Schrift, eigener
 * Hintergrund) und über eine gesperrte Spalte immer sichtbar.
 *
 * Auswertungsebene (params.level, siehe FilterBar.jsx/Statistics/Index.jsx):
 * bei "parent" liefert der Backend-Endpunkt bereits eine Zeile je Übernummer
 * mit summierten Mengen (Zielbestand, Meldebestand, Sicherheitsbestand,
 * aktueller Bestand, Paletten) über alle Farbvarianten - für die
 * Bestellpraxis oft die richtigere Ebene, weil sortimentsweise bestellt
 * wird. Die Tabelle selbst rechnet nichts um, nur die Spaltentexte ändern
 * sich mit der Ebene.
 */
export default function InventoryDetailGrid({params, groupOptions = []}) {
    const level = params?.level === "parent" ? "parent" : "article";
    const dataUrl = useMemo(() => withAssumptionParams("/statistics/inventory-strategy/data", params), [params]);
    const exportUrl = useMemo(() => withAssumptionParams("/statistics/inventory-strategy/export", params), [params]);
    const groupsUrl = useMemo(() => withAssumptionParams("/statistics/inventory-strategy/groups", params), [params]);
    const groupsByParentUrl = useMemo(() => withAssumptionParams("/statistics/inventory-strategy/groups-by-parent", params), [params]);
    const groupingOptions = useMemo(
        () => [
            {
                id: "article_group",
                label: "Warengruppe",
                columnId: "article_group",
                groupsUrl,
                groupField: "warengruppe",
                noneLabel: "Ohne Warengruppe",
                pluralLabel: "Warengruppen",
            },
            {
                id: "parent_article_number",
                label: "Übernummer",
                columnId: "parent_article_number",
                groupsUrl: groupsByParentUrl,
                groupField: "parent_article_number",
                noneLabel: "Ohne Übernummer",
                pluralLabel: "Übernummern",
            },
        ],
        [groupsUrl, groupsByParentUrl]
    );

    const [initialSorting, setInitialSorting] = useState([{id: "target_stock", desc: true}]);
    const handleStateChange = useCallback((state) => {
        if (Array.isArray(state?.sorting) && state.sorting.length) {
            setInitialSorting(state.sorting);
        }
    }, []);

    const columns = useMemo(
        () => [
            columnHelper.accessor("article_number", {
                id: "article_number",
                header: level === "parent" ? "Übernummer" : "Artikelnummer",
                meta: {
                    width: 150,
                    filterVariant: "text",
                    help:
                        level === "parent"
                            ? "Übernummer (Sammelartikel über allen Farbvarianten) - die Mengen dieser Zeile sind die Summe über alle Varianten."
                            : "Artikelnummer (SKU).",
                },
            }),
            columnHelper.accessor("name", {
                id: "name",
                header: "Bezeichnung",
                cell: (info) => info.getValue() || "–",
                meta: {width: 260, filterVariant: "text", help: "Artikelbezeichnung."},
            }),
            columnHelper.accessor("parent_article_number", {
                id: "parent_article_number",
                header: "Übernummer",
                cell: (info) => info.getValue() || "–",
                meta: {
                    width: 130,
                    filterVariant: "text",
                    help: "Übernummer (Sammelartikel über allen Farbvarianten dieses Artikels).",
                },
            }),
            columnHelper.accessor("parent_name", {
                id: "parent_name",
                header: "Übernummer-Bezeichnung",
                cell: (info) => info.getValue() || "–",
                meta: {width: 220, filterVariant: "text", help: "Bezeichnung der Übernummer."},
            }),
            columnHelper.accessor("article_group", {
                id: "article_group",
                header: "Warengruppe",
                cell: (info) => info.getValue() || "–",
                meta: {
                    width: 140,
                    filterVariant: "select",
                    selectOptions: [
                        {value: "", label: "Alle"},
                        {value: "__none__", label: "– (ohne Warengruppe)"},
                        ...groupOptions,
                    ],
                    help: "Warengruppe des Artikels. Über den Filter-Schalter oben lässt sich die Tabelle nach dieser Spalte gruppieren.",
                },
            }),
            columnHelper.accessor("abc_class", {
                id: "abc_class",
                header: "ABC",
                cell: (info) => {
                    const value = info.getValue();
                    return value ? (
                        <span className="badge" style={{backgroundColor: ABC_COLORS[value] || OTHER_GROUP_COLOR}}>{value}</span>
                    ) : "–";
                },
                meta: {
                    width: 70,
                    filterVariant: "select",
                    selectOptions: [
                        {value: "", label: "Alle"},
                        {value: "A", label: "A"},
                        {value: "B", label: "B"},
                        {value: "C", label: "C"},
                    ],
                    help: "A bis 80 % kumuliertem Umsatzanteil, B bis 95 %, C der Rest.",
                },
            }),
            columnHelper.accessor("net_amount_12m", {
                id: "net_amount_12m",
                header: "Absatz 12 Monate",
                cell: (info) => formatEuro(info.getValue()),
                meta: {width: 140, filterVariant: "number", help: "Nettoumsatz des Betrachtungszeitraums (Standard 12 Monate), bezogen auf den jüngsten Beleg im Datenbestand."},
            }),
            columnHelper.accessor("target_stock", {
                id: "target_stock",
                header: "Empf. Zielbestand",
                cell: (info) => (
                    <span className="badge bg-primary-subtle text-primary-emphasis fw-semibold px-2 py-1" style={{fontSize: "0.9rem"}}>
                        {formatQuantity(info.getValue())} Stk.
                    </span>
                ),
                meta: {
                    width: 150,
                    filterVariant: "number",
                    help:
                        "Wichtigste Spalte: empfohlener Zielbestand = Tagesabsatz × (Lieferzeit + Bestellzyklus) + Sicherheitsbestand. Beruht allein auf dem Absatz, unabhängig vom heutigen Bestand." +
                        (level === "parent" ? " Auf dieser Ebene die Summe über alle Farbvarianten der Übernummer - für sortimentsweise Bestellungen oft die richtigere Menge." : ""),
                },
            }),
            columnHelper.accessor("reorder_point", {
                id: "reorder_point",
                header: "Meldebestand",
                cell: (info) => `${formatQuantity(info.getValue())} Stk.`,
                meta: {width: 130, filterVariant: "number", help: "Bestand, bei dessen Unterschreiten nachbestellt werden sollte = Tagesabsatz × Lieferzeit + Sicherheitsbestand."},
            }),
            columnHelper.accessor("safety_stock", {
                id: "safety_stock",
                header: "Sicherheitsbestand",
                cell: (info) => `${formatQuantity(info.getValue())} Stk.`,
                meta: {width: 140, filterVariant: "number", help: "Puffer gegen Nachfrage- und Lieferzeitschwankungen, abhängig von der gewählten Lieferfähigkeit."},
            }),
            columnHelper.accessor("current_stock", {
                id: "current_stock",
                header: "Aktueller Bestand",
                cell: (info) => (
                    <StockByWarehousePopover stockByWarehouse={info.row.original.stock_by_warehouse}>
                        {formatQuantity(info.getValue())} Stk.
                    </StockByWarehousePopover>
                ),
                meta: {width: 140, filterVariant: "number", help: "Bestand nur im Lager SNV Weilheim (siehe Hinweis zur Bestandsquelle). Aufschlüsselung je Lager durch Antippen bzw. beim Überfahren mit der Maus."},
            }),
            columnHelper.accessor("coverage_days", {
                id: "coverage_days",
                header: "Reichweite (Tage)",
                cell: (info) => (info.getValue() === null ? "–" : formatQuantity(info.getValue())),
                meta: {width: 130, filterVariant: "number", help: "Aktueller Bestand geteilt durch den durchschnittlichen Tagesabsatz. Ohne Absatz im Betrachtungszeitraum leer."},
            }),
            columnHelper.accessor("pallets_target", {
                id: "pallets_target",
                header: "Paletten empfohlen",
                cell: (info) => formatPallets(info.getValue()),
                meta: {width: 140, filterVariant: "number", help: "Empfohlener Zielbestand umgerechnet in Paletten - nur für Warengruppen mit bekannter Palettengröße."},
            }),
            columnHelper.accessor("recommendation", {
                id: "recommendation",
                header: "Einstufung",
                cell: (info) => <RecommendationBadge value={info.getValue()} reason={info.row.original.recommendation_reason} />,
                meta: {
                    width: 170,
                    filterVariant: "select",
                    selectOptions: RECOMMENDATION_SELECT_OPTIONS,
                    help: "Empfohlene Handlung. Symbol und Text zeigen die Einstufung, die Begründung erscheint durch Antippen bzw. beim Überfahren mit der Maus.",
                },
            }),
        ],
        [groupOptions, level]
    );

    return (
        <DataGrid
            key={dataUrl}
            columns={columns}
            getRowId={(row) => row.article_number}
            initialSorting={initialSorting}
            onStateChange={handleStateChange}
            lockedColumnIds={["article_number", "name"]}
            groupingOptions={groupingOptions}
            emptyLabel="Keine Artikel für die gewählten Rechenannahmen und Filter gefunden."
            dataUrl={dataUrl}
            exportUrl={exportUrl}
            itemLabel="Artikel"
            itemLabelDative="Artikeln"
            defaultPageSize={100}
            compactHeader
        />
    );
}
